import { forwardRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { saveWaitlistEntry, type Concern } from '../../../lib/waitlist';
import type { TierInfo } from '../data/questions';

interface WaitlistFormProps {
  awarenessScore?: number;
  awarenessTier?: TierInfo;
}

const CONCERN_OPTIONS: { value: Concern; label: string }[] = [
  { value: 'phishing', label: 'Phishing (email)' },
  { value: 'smishing', label: 'Smishing (SMS)' },
  { value: 'vishing', label: 'Vishing (voice)' },
  { value: 'all', label: 'All of the above' },
];

export const WaitlistForm = forwardRef<HTMLElement, WaitlistFormProps>(
  function WaitlistForm({ awarenessScore, awarenessTier }, ref) {
    const [form, setForm] = useState({
      fullName: '',
      email: '',
      company: '',
      role: '',
      concern: '' as Concern | '',
      consent: false,
    });
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      const { name, value, type } = e.target;
      const next =
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : value;
      setForm((prev) => ({ ...prev, [name]: next }));
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!form.fullName.trim() || !form.email.trim() || !form.concern || !form.consent) {
        setError('Please fill in the required fields and confirm consent to be contacted.');
        return;
      }

      saveWaitlistEntry({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        company: form.company.trim() || undefined,
        role: form.role.trim() || undefined,
        concern: form.concern,
        consent: form.consent,
        awarenessScore,
        awarenessTier: awarenessTier?.tier,
      });

      setSubmitted(true);
    };

    return (
      <section
        id="waitlist"
        ref={ref}
        className="relative py-24 bg-gradient-to-b from-black to-gray-900"
      >
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 border border-blue-500/30 rounded-full px-4 py-1 mb-6">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-sm text-blue-300">AI detection · early access</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-white mb-4 leading-tight">
              Join the{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                AI detection
              </span>{' '}
              waitlist
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto leading-relaxed">
              Be first in line when Driscop's AI detection rolls out. We'll only reach out about the feature, no other marketing.
            </p>
          </div>

          {submitted ? (
            <div className="bg-gray-900/50 backdrop-blur-sm border border-green-500/30 rounded-2xl p-10 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-500/10 border border-green-500/40 flex items-center justify-center">
                <i className="ri-check-line text-3xl text-green-400"></i>
              </div>
              <h3 className="text-2xl font-medium text-white mb-3">You're on the list.</h3>
              <p className="text-gray-400 max-w-md mx-auto">
                Thanks {form.fullName.split(' ')[0]}. We'll email{' '}
                <span className="text-blue-300">{form.email}</span> when AI detection is ready for early testers.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Full name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                    placeholder="jane@company.co.uk"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Company <span className="text-gray-600">(optional)</span></label>
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                    placeholder="Acme Ltd"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Role / title <span className="text-gray-600">(optional)</span></label>
                  <input
                    type="text"
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none transition-colors"
                    placeholder="IT Manager"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Main concern *</label>
                <div className="relative">
                  <select
                    name="concern"
                    value={form.concern}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 pr-10 text-white focus:border-blue-500 focus:outline-none transition-colors appearance-none"
                  >
                    <option value="" className="bg-gray-900">Select what worries you most</option>
                    {CONCERN_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-gray-900">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              <label className="flex items-start gap-3 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 accent-blue-500 cursor-pointer"
                />
                <span>
                  I'm happy for Driscop to contact me about the AI detection feature. I can opt out at any time. See our{' '}
                  <Link to="/privacy" className="text-blue-300 hover:text-blue-200 underline">
                    Privacy Policy
                  </Link>
                  . *
                </span>
              </label>

              {error && (
                <div className="text-red-400 text-sm">{error}</div>
              )}

              <button
                type="submit"
                className="w-full bg-white text-black hover:bg-blue-400 hover:text-white py-4 rounded-xl font-medium transition-all duration-300 cursor-pointer"
              >
                Join the waitlist
              </button>

              <p className="text-xs text-gray-500 text-center">
                * required. We never share your details with third parties.
              </p>
            </form>
          )}
        </div>
      </section>
    );
  }
);
